import { formatDate } from 'v3/utils/controlTowerDate';
import { validSummary } from 'v3/components/ControlTower/TravelResources/contract';
export { formatDate };
export const KANBAN_PATH = '/kanban-cargas';
export const PER_PAGE = 25;
export const GROUPS = [
  {
    code: 'AVAILABLE',
    label: 'Pré Cargas / Cargas',
    integration: 'availableList',
    color: '#1C4B75',
  },
  { code: 'ATTENDED', label: 'Atendidas', integration: 'attendedList', color: '#099b94' },
  { code: 'IN_TRANSIT', label: 'Em Viagem', integration: 'inTransitList', color: '#29096c' },
  {
    code: 'AT_DESTINATION',
    label: 'No Destino',
    integration: 'atDestinationList',
    color: '#287d45',
  },
];
export const KANBAN_PERMISSIONS = [
  'VISUALIZAR_KANBAN_CARGAS',
  'VISUALIZAR_CARGAS',
  'VISUALIZAR_VIAGENS',
];
export const canReadKanban = (permission) => KANBAN_PERMISSIONS.every((slug) => permission(slug));
const decimal = (value) => typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value);
const count = (value) => Number.isSafeInteger(value) && value >= 0;
export function readPages(search) {
  const params = new URLSearchParams(search);
  return Object.fromEntries(
    GROUPS.map(({ code }) => {
      const raw = params.get(`p_${code}`) || '1';
      const page = /^[1-9]\d*$/.test(raw) ? Number(raw) : 1;
      return [code, page <= 1000000 ? page : 1];
    }),
  );
}
export function formatDecimal(value) {
  if (value === null || value === undefined || !/^-?\d+(\.\d+)?$/.test(String(value)))
    return 'Não informado';
  const [integer, fraction] = String(value).split('.');
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (fraction ? `,${fraction}` : '');
}
export function formatMoney(value, currency = 'BRL') {
  if (!decimal(value)) return 'Não informado';
  const prefix = currency === 'BRL' ? 'R$' : currency || 'Moeda não informada';
  return `${prefix} ${formatDecimal(value)}`;
}
export function formatDocument(value) {
  if (!value) return 'Não informado';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 14)
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (digits.length === 11) return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return value;
}
/** Rejeita resposta inválida em vez de representar falha como quadro vazio. */
export class KanbanContractError extends Error {}
export function assertUniqueItems(groups) {
  const seen = new Set();
  groups.forEach((group) =>
    group.data.forEach((card) => {
      if (seen.has(card.key))
        throw new KanbanContractError('Os dados mudaram durante a consulta. Atualize o Kanban.');
      seen.add(card.key);
    }),
  );
}
const optionalText = (value) => value == null || typeof value === 'string';
const optionalObject = (value) =>
  value == null || (typeof value === 'object' && !Array.isArray(value));
const textFields = (value, fields) =>
  optionalObject(value) && fields.every((field) => optionalText(value?.[field]));
const optionalList = (value, fields) =>
  value == null ||
  (Array.isArray(value) &&
    value.every((item) => item && count(item.id) && item.id > 0 && textFields(item, fields)));
function validDetails(card) {
  return (
    (card.occurrenceSummary == null || validSummary(card.occurrenceSummary)) &&
    ['client', 'product', 'driver'].every((key) => textFields(card[key], ['name', 'cnpj'])) &&
    textFields(card.carrier, ['name']) &&
    textFields(card.status, ['code', 'name']) &&
    optionalText(card.driver?.phone) &&
    textFields(card.vehicle, ['plate']) &&
    optionalList(card.wagons, ['plate']) &&
    textFields(card.quantity, ['weight', 'volume', 'pallets']) &&
    textFields(card.observations, ['comments', 'additionalInfo']) &&
    [
      'externalLoadId',
      'travelNumber',
      'sourceCurrency',
      'scheduledPickupAt',
      'scheduledDeliveryAt',
      'updatedAt',
    ].every((key) => optionalText(card[key])) &&
    ['origin', 'destination'].every((key) =>
      textFields(card[key], ['city', 'province', 'arrivedAt', 'completedAt']),
    ) &&
    ['origins', 'destinations'].every((key) =>
      optionalList(card[key], ['city', 'province', 'scheduledAt', 'arrivedAt', 'completedAt']),
    ) &&
    optionalObject(card.vehicleProfile) &&
    ['vehicleTypes', 'bodyTypes', 'implementTypes'].every((key) =>
      optionalList(card.vehicleProfile?.[key], ['name']),
    ) &&
    optionalObject(card.tracking) &&
    textFields(card.tracking?.tracker, ['name']) &&
    optionalList(card.tracking?.riskManagers, ['name']) &&
    (card.tracking?.required == null || typeof card.tracking.required === 'boolean')
  );
}
// Identidade e campos compartilhados pelo Kanban e pela lista consolidada.
export function validCard(card, groupCode) {
  return !!(
    card &&
    ['LOAD', 'PRE_LOAD', 'TRAVEL'].includes(card.type) &&
    count(card.id) &&
    card.id > 0 &&
    count(card.loadId) &&
    card.loadId > 0 &&
    card.key === `${card.type}:${card.id}` &&
    card.group === groupCode &&
    (card.type === 'TRAVEL'
      ? card.travelId === card.id && groupCode !== 'AVAILABLE'
      : card.id === card.loadId && groupCode === 'AVAILABLE') &&
    (card.amount === null || decimal(card.amount)) &&
    (card.sourceAmount === null || decimal(card.sourceAmount)) &&
    card.currency === 'BRL' &&
    validDetails(card)
  );
}
export function validateResponse(data, requested) {
  const expected = requested.group ? [requested.group] : GROUPS.map((g) => g.code);
  const fail = () => {
    throw new KanbanContractError('Resposta inválida do Kanban. Tente atualizar a consulta.');
  };
  if (
    data?.version !== 1 ||
    formatDate(data.asOf) === 'Não informado' ||
    !Array.isArray(data.groups) ||
    data.groups.length !== expected.length
  )
    fail();
  const seen = new Set();
  data.groups.forEach((group) => {
    if (
      !group ||
      !expected.includes(group.code) ||
      seen.has(group.code) ||
      !count(group.totalCount) ||
      !decimal(group.knownAmount) ||
      !count(group.missingAmountCount) ||
      group.missingAmountCount > group.totalCount ||
      group.currency !== 'BRL' ||
      group.amountComplete !== (group.missingAmountCount === 0) ||
      group.totalAmount !== (group.amountComplete ? group.knownAmount : null) ||
      !Array.isArray(group.data) ||
      group.data.length > requested.perPage
    )
      fail();
    seen.add(group.code);
    const pagination = group.pagination;
    if (
      pagination?.page !== requested.page ||
      pagination?.perPage !== requested.perPage ||
      pagination.totalPages !== Math.ceil(group.totalCount / requested.perPage) ||
      pagination.hasMore !== requested.page * requested.perPage < group.totalCount
    )
      fail();
    const keys = new Set();
    const expectedCount = Math.min(
      requested.perPage,
      Math.max(0, group.totalCount - (requested.page - 1) * requested.perPage),
    );
    if (group.data.length !== expectedCount) fail();
    group.data.forEach((card) => {
      if (!validCard(card, group.code) || keys.has(card.key)) fail();
      keys.add(card.key);
    });
  });
  assertUniqueItems(data.groups);
  return data;
}
